import { MapStyles } from '@methanesat/maps';
import { DateOption } from '@methanesat/ui-components';

import {
    isPlatform,
    isAreaEmissionsProduct,
    Platforms,
    AreaEmissionsProducts,
    STACCollection,
    UrlFeatureTypes,
    UrlViewState
} from '../types';
import { getDateRangeEnd, getDateRangeFromPlatform, getDateRangeStart } from './stac/stacSearch';
import { getMakesValidDate, isDate, parseDateString } from './time';
import { MSATL4RasterColorMaps } from '../consts';
import { isMSATL4RasterColormapName } from '../consts/colors';

export type QueryStringObject = Record<string, string>;
export function isQueryStringObject(data: unknown): data is QueryStringObject {
    if (typeof data === 'object' && data && Object.keys(data).length > 0) return true;
    return false;
}

/**
 * Defines the information needed to add one piece of state to
 * the querystring correctly and later, parse the querystring (could be comma-separated)
 * and load the parsed data into the store.
 */
export type StringValidator = (validateItem: string) => boolean;
export type ObjectValidator = (validateItem: QueryStringObject) => boolean;

/**
 * Checks if the URL date parameter is a valid date, is in the format YYYY-MM-DD,
 * is in the date picker, and is not before 2020 or in the future.
 */
export function validateUrlGlobalDateString(unsanitizedDateString: string, dateOptions: DateOption[]) {
    const isValidDateFormat = validateUrlDateString(unsanitizedDateString);
    if (!isValidDateFormat) return false;

    const utcDate = parseDateString(unsanitizedDateString);
    if (!utcDate) return false;

    const sanitizedDateString = getSanitizedURLDateFormat(unsanitizedDateString);

    const isInTimeRange = 2020 <= utcDate.getUTCFullYear() && utcDate.getTime() <= Date.now();
    const isInDatePicker = dateOptions.some(
        (dateOption) => getSanitizedURLDateFormat(dateOption.value) === sanitizedDateString
    );

    return isValidDateFormat && isInTimeRange && isInDatePicker;
}

/**
 * Checks that the URL feature-date parameter is a valid date in the format YYYY-MM-DD
 * and is within the range of the global date selected
 */
export const validateUrlFeatureDateString = (
    unsanitizedDateString: string,
    globalDate: number,
    platform: Platforms
) => {
    const isValidDateFormat = validateUrlDateString(unsanitizedDateString);
    if (!isValidDateFormat) return false;

    const dateRange = getDateRangeFromPlatform(platform);

    const utcDate = parseDateString(unsanitizedDateString);
    const utcNumber = utcDate?.getTime();
    if (!utcNumber) return false;

    // get start & end of platform-specific time ranges
    const rangeEnd = getDateRangeEnd(globalDate, dateRange);
    const rangeStart = getDateRangeStart(globalDate, dateRange);

    const isInTimeRange = rangeStart.getTime() <= utcNumber && utcNumber <= rangeEnd.getTime();
    return isInTimeRange;
};

/**
 * Checks that the URL feature-date parameter is a valid date in the format YYYY-MM-DD
 */
export const validateUrlDateString = (unsanitizedDateString: string) => {
    // Regular expression pattern for date formats: YYYY-MM-DD, YYYY-M-D, YYYY-MM-D
    const regex = /^\d{4}-\d{1,2}-\d{1,2}$/;

    // Check if the date string is valid format before parsing
    if (!regex.test(unsanitizedDateString)) return false;

    const utcDate = parseDateString(unsanitizedDateString);

    // Check if parsed date is valid
    return !!utcDate && !isNaN(utcDate.getTime());
};

/**
 * Checks if the product url parameter is either "L3 or L4"
 */
export function validateUrlProductParam(unsanitizedProduct: string): unsanitizedProduct is AreaEmissionsProducts {
    return isAreaEmissionsProduct(unsanitizedProduct);
}

/**
 * Checks if the platform parameter is either "MethaneSAT or MethaneAIR"
 */
export function validateUrlPlatformParam(unsanitizedPlatform: string): unsanitizedPlatform is Platforms {
    return isPlatform(unsanitizedPlatform);
}

/**
 * Checks if the L4 MSAT color scale name parameter is either "msat_warm_default" or "msat_warm_extended"
 */
export function validateL4MSATColorScaleParam(
    unsanitizedColorScaleName: string
): unsanitizedColorScaleName is MSATL4RasterColorMaps {
    return isMSATL4RasterColormapName(unsanitizedColorScaleName);
}

/**
 * Checks if the URL collection-id parameter is a contained within the STACCollection enum.
 */
export function validateUrlCollectionId(unsanitizedCollectionId: string): unsanitizedCollectionId is STACCollection {
    return Object.values(STACCollection).includes(unsanitizedCollectionId as STACCollection);
}

/**
 * Used for STAC itemId and target ids.
 *
 * This only checks that the parameter is a non-empty string since
 * naming conventions for STAC items are still in the works. Once those are finalized
 * stricter validation should be implemented.
 */
export function validateUrlString(unsanitizedString: string) {
    return typeof unsanitizedString === 'string' && unsanitizedString.length > 0;
}

/**
 * Checks if the URL feature-type parameter is a valid feature type.
 */
export function validateUrlFeatureType(unsanitizedFeatureType: string) {
    return Object.values(UrlFeatureTypes).includes(unsanitizedFeatureType as UrlFeatureTypes);
}

export function validateUrlViewState(viewState: UrlViewState) {
    for (const [key, value] of Object.entries(viewState)) {
        const newValue = parseFloat(value);
        if (isNaN(newValue) || (key === 'latitude' && Math.abs(newValue) > 90)) {
            return false;
        }
    }
    return true;
}

export function validateUrlMapStyles(mapStyle: string) {
    return Object.values(MapStyles).includes(mapStyle as (typeof MapStyles)[keyof typeof MapStyles]);
}

export const getSanitizedURLDateFormat = (input: unknown) => {
    let utcDate: Date | null = null;

    const makesValidDate = getMakesValidDate(input);
    if (!makesValidDate) return null;

    if (typeof input === 'string') {
        utcDate = parseDateString(input);
    } else if (input && typeof input === 'object' && isDate(input)) {
        utcDate = new Date(input);
    } else if (typeof input === 'number') {
        utcDate = new Date(input);
    }
    if (utcDate && isDate(utcDate)) {
        return utcDate.toISOString().split('T')[0];
    }
    return null;
};
